import RosterSyncStatus from 'components/People/RosterSyncStatus';
import { useAuth } from 'contexts/auth';
import { useTranslation } from 'react-i18next';
import {
    Button,
    Checkbox,
    Container,
    Dropdown,
    Icon,
    IconButton,
    Paper,
    ScrollableTable,
    ScrollableTableBody,
    ScrollableTableCell,
    ScrollableTableHead,
    ScrollableTableRow,
    ScrollableTableSortableHeaderCells,
    Stack,
    styled,
    Text,
    TextInput,
} from 'shell/components';
import { getPrettyRole, getPrettySections, getUserPreferredFullName } from 'utils/helper';

const Desktop = ({
    accountUsers,
    sortState,
    handleSetSortState,
    filters,
    setFilters,
    getFilterCount,
    checked,
    setChecked,
    rosterSyncStatus,
    rosterSyncError,
    rosterSyncProgress,
    isRosterSyncing,
    downloadCourseRoster,
    setSyncRosterDialog,
    getOptions,
    handleSelect,
    columns,
}) => {
    const { auth } = useAuth();
    const { t } = useTranslation();

    return (
        <Container device={'desktop'}>
            <RootStack spacing={5}>
                <Stack alignItems={'center'} direction={'row'} justifyContent={'space-between'}>
                    <Text component={'h1'} variant={'h6'} weight={'semibold'}>
                        {t('people.title', 'People')}
                    </Text>
                    <Stack direction={'row'} spacing={4}>
                        <Button
                            sx={{ flexShrink: 0 }}
                            color={'neutral'}
                            startIcon={<Icon kind={'filter'} />}
                            variant={'secondary'}
                            onClick={() => setFilters((current) => ({ ...current, open: true }))}
                        >
                            {t('controls.filters', 'Filters')} ({getFilterCount(filters, sortState)})
                        </Button>
                        <TextInput
                            endIcon={<Icon kind={'search'} />}
                            placeholder={t('controls.search', 'Search')}
                            value={filters.search}
                            onChange={(e) => setFilters((current) => ({ ...current, search: e.target.value }))}
                        />
                        {auth?.role === 'instructor' && (
                            <Button
                                sx={{ flexShrink: 0 }}
                                disabled={isRosterSyncing}
                                startIcon={<Icon kind={'resync'} />}
                                onClick={() => setSyncRosterDialog({ open: true })}
                            >
                                {isRosterSyncing
                                    ? t('controls.syncing-roster', 'Syncing Roster...')
                                    : t('controls.sync-roster', 'Sync Roster')}
                            </Button>
                        )}
                    </Stack>
                </Stack>
                <RosterSyncStatus status={rosterSyncStatus} error={rosterSyncError} progress={rosterSyncProgress} />
                <CustomPaper sx={{ flex: 1 }} elevation={0}>
                    <Stack spacing={5}>
                        <Stack direction={'row-reverse'}>
                            <Button
                                startIcon={<Icon kind={'download'} />}
                                variant={'tertiary'}
                                onClick={downloadCourseRoster}
                            >
                                {t('people.download-course-roster', 'Download Course Roster')}
                            </Button>
                        </Stack>
                        <CustomScrollableTable aria-label={'people'}>
                            <ScrollableTableHead>
                                <ScrollableTableRow>
                                    <ScrollableTableCell isFixed>
                                        <Checkbox
                                            aria-label={'select all'}
                                            checked={
                                                Boolean(accountUsers.length) &&
                                                accountUsers.every(({ owner_account_id }) =>
                                                    checked.includes(owner_account_id)
                                                )
                                            }
                                            onChange={(e) => {
                                                setChecked(
                                                    e.target.checked
                                                        ? accountUsers.map(({ owner_account_id }) => owner_account_id)
                                                        : []
                                                );
                                            }}
                                        />
                                    </ScrollableTableCell>
                                    <ScrollableTableSortableHeaderCells
                                        columns={columns}
                                        sortState={sortState}
                                        handleSetSortState={handleSetSortState}
                                    />
                                    <ScrollableTableCell isFixed>{t('table.actions', 'Actions')}</ScrollableTableCell>
                                </ScrollableTableRow>
                            </ScrollableTableHead>
                            <ScrollableTableBody>
                                {accountUsers.map((accountUser) => {
                                    const accessibleName =
                                        `${getUserPreferredFullName(accountUser)} (${accountUser.owner_account_id})`.trim();
                                    const lastLoginDate = accountUser.login_activity
                                        ? t('common:ui.format-date-short', {
                                              val: accountUser.login_activity.created_on,
                                          })
                                        : '--';
                                    return (
                                        <ScrollableTableRow key={accountUser.owner_account_id}>
                                            <ScrollableTableCell isFixed>
                                                <Checkbox
                                                    aria-label={`select ${accessibleName}`}
                                                    checked={checked.includes(accountUser.owner_account_id)}
                                                    onChange={(e) => {
                                                        setChecked((current) => {
                                                            return e.target.checked
                                                                ? [...current, accountUser.owner_account_id]
                                                                : current.filter(
                                                                      (item) => item !== accountUser.owner_account_id
                                                                  );
                                                        });
                                                    }}
                                                />
                                            </ScrollableTableCell>
                                            <ScrollableTableCell isFixed>
                                                {getUserPreferredFullName(accountUser)}
                                            </ScrollableTableCell>
                                            <ScrollableTableCell>{accountUser.email}</ScrollableTableCell>
                                            <ScrollableTableCell>
                                                {getPrettyRole(accountUser.role, t)}
                                            </ScrollableTableCell>
                                            <ScrollableTableCell>
                                                {getPrettySections(accountUser.sections, t)}
                                            </ScrollableTableCell>
                                            <ScrollableTableCell>{lastLoginDate}</ScrollableTableCell>
                                            <ScrollableTableCell isFixed>
                                                <Dropdown
                                                    sx={{ mx: 'auto', width: 'fit-content' }}
                                                    id={`dropdown-${accountUser.owner_account_id}`}
                                                    options={getOptions(accountUser)}
                                                    rootComponent={
                                                        <IconButton
                                                            aria-label={`more options for ${accessibleName}`}
                                                            kind={'moreHorizontal'}
                                                            size={'small'}
                                                            variant={'square'}
                                                        />
                                                    }
                                                    onSelect={({ key }) => handleSelect({ key }, accountUser)}
                                                />
                                            </ScrollableTableCell>
                                        </ScrollableTableRow>
                                    );
                                })}
                            </ScrollableTableBody>
                        </CustomScrollableTable>
                    </Stack>
                </CustomPaper>
            </RootStack>
        </Container>
    );
};

const RootStack = styled(Stack)(() => {
    return {
        flex: 1,
        width: '100%',
    };
});

const CustomPaper = styled(Paper)(({ theme }) => {
    return {
        borderRadius: '12px 12px 0 0',
        padding: theme.spacing(5, 5, 0, 5),
    };
});

const CustomScrollableTable = styled(ScrollableTable)(({ theme }) => {
    return {
        flex: 1,
        '& .MuiTableCell-root': {
            '&:nth-child(1)': {
                left: 0,
                minWidth: '73px',
                maxWidth: '73px',
                width: '73px',
            },
            '&:nth-child(2)': {
                left: 73,
                minWidth: '250px',
                maxWidth: '250px',
                width: '250px',
            },
            '&:last-child': {
                right: 0,
                borderLeft: `1px solid ${theme.palette.ensightful.neutral[300]}`,
                minWidth: '85px',
                width: '85px',
            },
            '&:nth-last-child(2)': {
                borderRight: 'none',
            },
        },
    };
});

export default Desktop;
